"use client";

import Image from "next/image";
import Link from "next/link";
import React, {
    type HTMLAttributes,
    Suspense,
    useEffect,
    useState,
    type ReactElement,
} from "react";
import { twMerge } from "tailwind-merge";

import { TYPO_STYLES } from "@/styles/typography";
import { VIEWPORT_STYLES } from "@/styles/viewport";
import { WRAPPER_STYLES } from "@/styles/wrapper";

import { NavigationBottomView } from "./header-navigation-bottom.view";
import { NavigationTopView } from "./header-navigation-top.view";
import { HeaderSearchFieldView } from "./header-search-field.view";
import { type NavigationItem } from "./scheme";

// Defined classes for x-translation depending on current menu level. Add more classes for deeper menus
const MENU_LEVEL_CLASSES = [
    "-translate-x-0",
    "-translate-x-full",
    "-translate-x-full2",
    "-translate-x-full3",
];

type HeaderModuleProps = {
    navigationBottom: NavigationItem[];
    navigationTop: NavigationItem[];
};

export const HeaderModuleView = ({
    navigationTop,
    navigationBottom,
}: HeaderModuleProps): ReactElement => {
    const [selectedNavigationLevel, setSelectedNavigationLevel] = useState(0);
    const [scrollOnTop, setScrollOnTop] = useState(true);
    const [mobileNavigationVisibile, setMobileNavigationVisibile] = useState(false);

    useEffect(() => {
        window.addEventListener("scroll", () => setScrollOnTop(window.scrollY <= 0));
        setScrollOnTop(window.scrollY <= 0);
        window.addEventListener("resize", () => {
            setSelectedNavigationLevel(0);
            setMobileNavigationVisibile(false);
        });
        // ToDo Remove listener
    }, []);

    const handleBurgerClick = () => {
        setMobileNavigationVisibile((v) => !v);
    };

    const handleLevelClick = (level: number) => {
        setSelectedNavigationLevel(level);
    };

    return (
        <header className={twMerge(scrollOnTop ? "md:bg-grey1 " : "bg-white ")}>
            <SearchBarView
                mobileNavigationVisibile={mobileNavigationVisibile}
                onBurgerClick={handleBurgerClick}
                className="md:hidden"
            />
            <div className="relative overflow-x-hidden overflow-y-auto md:overflow-x-visible md:overflow-y-visible">
                <div
                    className={twMerge(
                        WRAPPER_STYLES.VTopStretch(),
                        mobileNavigationVisibile ? "flex" : "hidden md:flex",
                        MENU_LEVEL_CLASSES[selectedNavigationLevel],
                        "md:translate-x-0 transition-transform md:transition-none ease-in-out",
                    )}
                >
                    <nav className={twMerge("order-1 md:order-0 md:[&_div]:shadow-none")}>
                        <NavigationTopView
                            navigationItems={navigationTop}
                            onLevelClick={handleLevelClick}
                            navigationLevel={0}
                            selectedNavigationLevel={selectedNavigationLevel}
                            scrollOnTop={scrollOnTop}
                        />
                    </nav>
                    <SearchBarView
                        mobileNavigationVisibile={mobileNavigationVisibile}
                        onBurgerClick={handleBurgerClick}
                        className="hidden md:block md:order-1 md:mb-4"
                    />
                    <nav className={twMerge("order-0 md:order-2")}>
                        <NavigationBottomView
                            onLevelClick={handleLevelClick}
                            navigationItems={navigationBottom}
                            navigationLevel={0}
                            selectedNavigationLevel={selectedNavigationLevel}
                        />
                    </nav>
                </div>
            </div>
        </header>
    );
};

type SearchBarProps = {
    mobileNavigationVisibile: boolean;
    onBurgerClick: () => void;
};
const SearchBarView = ({
    onBurgerClick,
    mobileNavigationVisibile,
    className,
}: SearchBarProps & HTMLAttributes<HTMLDivElement>) => {
    return (
        <div className={twMerge("w-full shadow-md md:shadow-none", className)}>
            <div
                className={twMerge(
                    WRAPPER_STYLES.HCenterBetween(),
                    VIEWPORT_STYLES.viewport(),
                    "h-16 md:h-auto gap-8",
                )}
            >
                <Link href="/" prefetch={false} aria-label="logo">
                    <Image src="/logo.svg" alt="Logo Raabe" height={38} width={135} priority />
                </Link>
                <Suspense>
                    <HeaderSearchFieldView />
                </Suspense>
                <button
                    onClick={onBurgerClick}
                    className={twMerge(WRAPPER_STYLES.HCenterStretch(), "md:hidden")}
                >
                    <span
                        className={twMerge(
                            TYPO_STYLES.icon(),
                            "text-3xl",
                            VIEWPORT_STYLES.padding(),
                            VIEWPORT_STYLES.margin(true),
                        )}
                    >
                        {mobileNavigationVisibile
                            ? String.fromCharCode(Number("0xe91b"))
                            : String.fromCharCode(Number("0xe954"))}
                    </span>
                </button>
            </div>
        </div>
    );
};
